import * as Sentry from "@sentry/react";

import { ALLOWED_QUERY_PARAMETERS } from "./search-params-schema";

const DANGEROUS_PATTERNS = {
  fuzzing: /sssied|xss|xxe|sqlmap|acunetix|netsparker|burp|nikto/i,
  scriptInjection: /<script|javascript:|onerror=|onload=|onclick=/i,
  sqlInjection: /union\s+select|drop\s+table|insert\s+into|delete\s+from/i,
  pathTraversal: /\.\.\//,
  longValue: 1000,
} as const;

interface SecurityAnalysis {
  isSuspicious: boolean;
  reasons: string[];
  severity: "low" | "medium" | "high";
  shouldLog: boolean;
  parameterCount: number;
  allowedParamCount: number;
  unknownParamCount: number;
  suspiciousParams: Array<{
    name: string;
    value: string;
    reason: string;
  }>;
}

export function analyzeQueryParameters(searchParams: URLSearchParams): SecurityAnalysis {
  const reasons: string[] = [];
  const suspiciousParams: SecurityAnalysis["suspiciousParams"] = [];
  let severity: SecurityAnalysis["severity"] = "low";

  const paramEntries = Array.from(searchParams.entries());
  const parameterCount = paramEntries.length;

  let allowedParamCount = 0;
  let unknownParamCount = 0;

  for (const [name, value] of paramEntries) {
    const paramReasons: string[] = [];

    const isAllowed = ALLOWED_QUERY_PARAMETERS.includes(
      name as (typeof ALLOWED_QUERY_PARAMETERS)[number],
    );

    if (!isAllowed) {
      unknownParamCount++;
      paramReasons.push("Unknown parameter (not in whitelist)");
      severity = severity === "low" ? "medium" : severity;
    } else {
      allowedParamCount++;
    }

    if (DANGEROUS_PATTERNS.fuzzing.test(value)) {
      paramReasons.push("Fuzzing marker detected");
      severity = "high";
    }

    if (
      DANGEROUS_PATTERNS.scriptInjection.test(value) ||
      DANGEROUS_PATTERNS.scriptInjection.test(name)
    ) {
      paramReasons.push("Script injection pattern");
      severity = "high";
    }

    if (DANGEROUS_PATTERNS.sqlInjection.test(value)) {
      paramReasons.push("SQL injection pattern");
      severity = "high";
    }

    if (DANGEROUS_PATTERNS.pathTraversal.test(value)) {
      paramReasons.push("Path traversal pattern");
      severity = severity === "low" ? "medium" : severity;
    }

    if (value.length > DANGEROUS_PATTERNS.longValue) {
      paramReasons.push(`Excessive length: ${value.length} chars`);
      severity = severity === "low" ? "medium" : severity;
    }

    if (paramReasons.length > 0) {
      suspiciousParams.push({
        name,
        value: value.substring(0, 100),
        reason: paramReasons.join(", "),
      });
      reasons.push(`Parameter "${name}": ${paramReasons.join(", ")}`);
    }
  }

  if (unknownParamCount > 0) {
    reasons.unshift(
      `${unknownParamCount} unknown parameter(s) detected (allowed: ${allowedParamCount}, total: ${parameterCount})`,
    );
    if (unknownParamCount >= 5) {
      severity = "high";
    }
  }

  const isSuspicious = reasons.length > 0;
  const shouldLog = isSuspicious && (severity === "high" || severity === "medium");

  return {
    isSuspicious,
    reasons,
    severity,
    shouldLog,
    parameterCount,
    allowedParamCount,
    unknownParamCount,
    suspiciousParams,
  };
}

function logSuspiciousQueryParameters(
  analysis: SecurityAnalysis,
  pathname: string,
  fullUrl: string,
): void {
  if (!analysis.shouldLog) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag("security_event", "suspicious_query_parameters");
    scope.setTag("severity", analysis.severity);
    scope.setLevel(analysis.severity === "high" ? "warning" : "info");

    scope.setContext("query_analysis", {
      pathname,
      totalParameters: analysis.parameterCount,
      allowedParameters: analysis.allowedParamCount,
      unknownParameters: analysis.unknownParamCount,
      reasons: analysis.reasons,
      severity: analysis.severity,
      timestamp: new Date().toISOString(),
    });

    scope.setContext("suspicious_parameters", {
      count: analysis.suspiciousParams.length,
      parameters: analysis.suspiciousParams,
    });

    scope.setContext("request_info", {
      url: fullUrl,
      userAgent: navigator.userAgent,
      referrer: document.referrer,
    });

    const message = `Suspicious query parameters detected: ${analysis.reasons[0] || "Unknown threat"}`;
    Sentry.captureMessage(message, analysis.severity === "high" ? "warning" : "info");
  });
}

export function monitorQueryParameters(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const searchParams = new URLSearchParams(window.location.search);
    const analysis = analyzeQueryParameters(searchParams);

    if (analysis.isSuspicious) {
      // Only log to console in dev; in production Sentry capture is sufficient.
      if (import.meta.env.DEV) {
        console.warn("[Security] Suspicious query parameters detected:", {
          severity: analysis.severity,
          count: analysis.suspiciousParams.length,
          reasons: analysis.reasons,
        });
      }

      logSuspiciousQueryParameters(analysis, window.location.pathname, window.location.href);
    }
  } catch (error) {
    console.error("[Security] Error analysing query parameters:", error);
  }
}
